import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useConfiguration } from '../../../hooks/useConfiguration';
import { Card, CardContent, Button } from '../../../shared/components/ui';
import {
  AlertCircle,
  ChevronRight,
  Calculator,
  ChartBar,
  TrendingUp,
  Battery,
  Settings
} from 'lucide-react';
import { clsx } from 'clsx';
import { useCurrentProject, useProjectData } from '../../../hooks/useProjectData';
import { useProjectExchangeRate } from '../../../hooks/useProjectExchangeRate';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// Import components
import { AlgorithmSection } from '../components/analysis/AlgorithmSectionRefactored';
import { TheoreticalMaximumSection } from '../components/analysis/TheoreticalMaximumSectionRefactored';
import { SubStepsNavigation } from '../components/shared/SubStepsNavigation';

// Safe number helper to handle NaN values
const safeNumber = (value: any, defaultValue: number = 0): number => {
  if (value === null || value === undefined) return defaultValue;
  const num = Number(value);
  return isNaN(num) || !isFinite(num) ? defaultValue : num;
};

interface OpportunityMetrics {
  dailyPriceSpread: number;
  monthlyArbitrageOpportunity: number;
  yearlyArbitrageOpportunity: number;
  peakShavingPotential: number;
  achievablePeakReduction?: number;
  optimalChargingHours: string[];
  optimalDischargingHours: string[];
  averageChargePrice: number;
  averageDischargePrice: number;
  priceVolatility: number;
  recommendedBESSCapacity: number;
  totalConsumptionKwh: number;
  expensiveHoursConsumption: number;
  cheapHoursConsumption: number;
  expensiveHoursPercentage: number;
  priceSpreadPerMwh: number;
  efficiencyLosses: number;
  netArbitrageSavings: number;
  demandChargeRate: number;
  peakReductionValue: number;
  cyclesPerDay: number;
  batteryUtilization: number;
}

export function OptimizationAnalysisPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State for sections

  // Load project data from database
  const { data: currentProject } = useCurrentProject();
  const { data: projectData, isLoading: isLoadingProjectData } = useProjectData(currentProject?.id);

  // Load configuration from backend
  const { configuration, isLoading: isLoadingConfig } = useConfiguration(currentProject?.id);

  // Use the exchange rate from the Bill (Step 1) saved per project
  const { data: exchangeRateData } = useProjectExchangeRate(currentProject?.id);
  const exchangeRate = exchangeRateData?.project_rate || 900;
  
  const [activeSection, setActiveSection] = useState<'theoretical' | 'algorithm'>('theoretical');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [opportunity, setOpportunity] = useState<OpportunityMetrics | null>(null);
  const [isStartingOptimization, setIsStartingOptimization] = useState(false);

  useEffect(() => {
    if (projectData?.consumption && projectData?.bill && projectData?.prices) {
      analyzeOpportunity();
    }
  }, [projectData]);

  // Configuration now loads automatically via useConfiguration hook

  const analyzeOpportunity = async () => {
    setIsAnalyzing(true);
    
    setTimeout(() => {
      const priceData = projectData?.prices?.data || [];
      const consumptionData = projectData?.consumption?.data || [];
      
      // Get price statistics
      const priceValues = priceData.map(p => p.priceUsd || 0);
      const avgPrice = priceValues.reduce((a, b) => a + b, 0) / Math.max(1, priceValues.length);
      const sortedPrices = [...priceValues].sort((a, b) => a - b);
      const lowPriceThreshold = sortedPrices[Math.floor(sortedPrices.length * 0.25)] || 0;
      const highPriceThreshold = sortedPrices[Math.floor(sortedPrices.length * 0.75)] || 0;
      
      // Find charging hours (lowest 25% of prices) and calculate averages
      const chargingHours: string[] = [];
      const dischargingHours: string[] = [];
      let lowPriceSum = 0;
      let lowPriceCount = 0;
      let highPriceSum = 0;
      let highPriceCount = 0;
      
      priceData.forEach((price, index) => {
        const hour = new Date(price.timestamp).getHours();
        const priceValue = price.priceUsd || 0;
        
        if (priceValue <= lowPriceThreshold && priceValue > 0) {
          chargingHours.push(`${hour}:00`);
          lowPriceSum += priceValue;
          lowPriceCount++;
        } else if (priceValue >= highPriceThreshold) {
          dischargingHours.push(`${hour}:00`);
          highPriceSum += priceValue;
          highPriceCount++;
        }
      });
      
      // Calculate consumption during expensive hours
      let expensiveHoursConsumption = 0;
      let cheapHoursConsumption = 0;
      let totalConsumption = 0;
      
      consumptionData.forEach((consumption, index) => {
        // Using actual data structure: consumption.consumption (in kW, 15-min intervals)
        const consumptionKw = consumption.consumption || 0;
        const consumptionKwh = consumptionKw / 4; // Convert 15-min kW to kWh
        totalConsumption += consumptionKwh;
        
        if (index < priceData.length) {
          const price = priceData[index].priceUsd || 0;
          if (price >= highPriceThreshold) {
            expensiveHoursConsumption += consumptionKwh;
          } else if (price <= lowPriceThreshold) {
            cheapHoursConsumption += consumptionKwh;
          }
        }
      });
      
      // Use configured BESS capacity or recommend based on daily consumption
      const dailyConsumption = totalConsumption / Math.max(1, consumptionData.length / 24);
      const recommendedCapacity = configuration?.bessCapacityKwh || configuration?.maxEnergyKwh || Math.round(dailyConsumption * 0.3 / 100) * 100;
      
      // Calculate average prices correctly
      const avgLowPrice = lowPriceCount > 0 ? lowPriceSum / lowPriceCount : 0;
      const avgHighPrice = highPriceCount > 0 ? highPriceSum / highPriceCount : 0;
      
      const priceSpread = avgHighPrice - avgLowPrice;
      const efficiency = (configuration?.roundTripEfficiency || 94) / 100;
      const efficiencyLosses = (1 - efficiency) * 100;
      const cyclesPerDay = Math.min(2, Math.max(dischargingHours.length / 4, 1));
      
      // Calculate arbitrage opportunity - priceSpread is in USD/MWh, throughput in kWh
      const dailyThroughput = recommendedCapacity * cyclesPerDay; // kWh per day
      const dailyArbitrage = (dailyThroughput / 1000) * priceSpread * efficiency; // Convert kWh to MWh, multiply by spread
      const monthlyArbitrage = dailyArbitrage * 30;
      const yearlyArbitrage = dailyArbitrage * 365;
      
      // Calculate peak shaving potential (moved up to avoid reference error)
      const peakDemand = projectData?.consumption?.peakDemand || 0;
      const peakReduction = Math.min(peakDemand * 0.15, configuration?.maxPowerKw || recommendedCapacity / 2);
      const demandChargeRate = projectData?.bill?.demandHpRate ? projectData.bill.demandHpRate / exchangeRate : 10; // Convert CLP to USD
      const monthlyPeakSavings = peakReduction * demandChargeRate;
      
      // Calculate retail tariff value (moved up to avoid reference error)
      const retailTariffUsdPerMwh = projectData?.bill?.energyRate ? (projectData.bill.energyRate / exchangeRate * 1000) : 70; // Convert CLP/kWh to USD/MWh
      
      // For zero-price hours, the value is the avoided retail tariff
      // Calculate self-consumption savings
      const selfConsumptionSavings = (expensiveHoursConsumption / 1000) * retailTariffUsdPerMwh; // Value of avoiding retail purchases
      
      // Net savings including retail tariff value
      const netDailySavings = dailyArbitrage + (selfConsumptionSavings / 365);

      // All variables already calculated above, now set the opportunity state
      setOpportunity({
        dailyPriceSpread: priceSpread,
        monthlyArbitrageOpportunity: monthlyArbitrage,
        yearlyArbitrageOpportunity: yearlyArbitrage,
        peakShavingPotential: peakReduction,
        achievablePeakReduction: peakReduction * 0.8,
        optimalChargingHours: [...new Set(chargingHours)].slice(0, 8),
        optimalDischargingHours: [...new Set(dischargingHours)].slice(0, 8),
        averageChargePrice: avgLowPrice,
        averageDischargePrice: avgHighPrice,
        priceVolatility: (Math.max(...priceValues) - Math.min(...priceValues)) / avgPrice,
        recommendedBESSCapacity: recommendedCapacity,
        totalConsumptionKwh: totalConsumption,
        expensiveHoursConsumption,
        cheapHoursConsumption,
        expensiveHoursPercentage: (expensiveHoursConsumption / totalConsumption) * 100,
        priceSpreadPerMwh: priceSpread,
        efficiencyLosses,
        netArbitrageSavings: netDailySavings,
        demandChargeRate,
        peakReductionValue: monthlyPeakSavings,
        cyclesPerDay,
        batteryUtilization: cyclesPerDay / 2
      });
      
      setIsAnalyzing(false);
    }, 1500);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Main Header - Same as Step 2.1 */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          {t('optimizationAnalysis.header.title')}
        </h2>
        {currentProject && (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-full mt-3 mb-2">
            <div className="w-2 h-2 bg-brand-primary rounded-full"></div>
            <span className="text-sm font-medium text-gray-900">
              {t('optimizationAnalysis.header.workingOn', { projectName: currentProject.name })}
            </span>
          </div>
        )}
        <p className="text-gray-600">
          {t('optimizationAnalysis.header.description')}
        </p>
      </div>

      {/* Sub-step Progress Navigation */}
      <SubStepsNavigation />
      
      {/* Current Step Info - Same positioning as 2.1 */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-semibold text-black mb-2">
          {t('optimizationAnalysis.stepInfo.title')}
        </h1>
        <p className="text-sm text-gray-500">
          {t('optimizationAnalysis.stepInfo.description')}
        </p>
      </div>

      {/* Navigation Tabs - Clean Apple Style */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {['theoretical', 'algorithm'].map((section, idx) => (
                <button
                  key={section}
                  onClick={() => setActiveSection(section as any)}
                  className={clsx(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2',
                    activeSection === section
                      ? 'bg-white text-black shadow-sm border border-gray-200'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  {section === 'theoretical' && <TrendingUp className="w-4 h-4" />}
                  {section === 'algorithm' && <ChartBar className="w-4 h-4" />}
                  <span>
                    {section === 'theoretical' && t('optimizationAnalysis.tabs.theoreticalMaximum')}
                    {section === 'algorithm' && t('optimizationAnalysis.tabs.algorithm')}
                  </span>
                </button>
              ))}
            </div>

            {/* Run Simulation Button */}
            <Button
              variant="primary"
              size="md"
              onClick={async () => {
                // Build request body - only include ranges if configuration is loaded and has values
                const requestBody: any = {
                  retail_tariff: 70.0
                };

                // Only send power_range if configuration has the values
                if (configuration?.minPowerKw && configuration?.maxPowerKw) {
                  requestBody.power_range = [configuration.minPowerKw, configuration.maxPowerKw];
                }

                // Only send energy_range if configuration has the values
                if (configuration?.minEnergyKwh && configuration?.maxEnergyKwh) {
                  requestBody.energy_range = [configuration.minEnergyKwh, configuration.maxEnergyKwh];
                }

                // Add generator config if enabled
                if (configuration?.useGenerator) {
                  requestBody.generator_config = {
                    use_generator: true,
                    hours_per_day: configuration.generatorHoursPerDay || 4,
                    fuel_cost_per_liter: 1.0,
                    fuel_consumption_per_hour: 10,
                    maintenance_cost_per_hour: 5
                  };
                }

                // Trigger multi-config optimization and wait for backend confirmation before navigating
                setIsStartingOptimization(true);
                try {
                  const isManualMode = configuration?.autoSize === false;
                  const algorithmMode = configuration?.algorithmMode || 'auto';
                  const selectedAlgorithm = configuration?.selectedAlgorithm || 'perfect_foresight';
                  const isManualAlgorithm = algorithmMode === 'manual';

                  // Calculate total CAPEX including installation cost
                  const baseCAPEX = configuration?.capexPerKwh || 140;
                  const installationPercent = configuration?.installationCostPercent || 15;
                  const totalCAPEX = baseCAPEX * (1 + installationPercent / 100);

                  // Auto algorithm mode picks ONE planner (peak_shave is our current best
                  // default for clients with HP demand charges). Manual algorithm mode honors
                  // whatever the user selected in the 2.1 dropdown.
                  const requestBody: any = {
                    projectId: currentProject?.id,
                    capexPerKwh: totalCAPEX,
                    test_algorithms: false,
                    planner_type: isManualAlgorithm ? selectedAlgorithm : 'perfect_foresight_peak_shave'
                  };

                  if (isManualMode) {
                    // Manual size: Use single algorithm with user-specified size
                    const powerKw = configuration?.fixedPowerKw || 1000;
                    const energyKwh = configuration?.fixedEnergyKwh || 4000;
                    requestBody.manual_configurations = [{
                      power_kw: powerKw,
                      energy_kwh: energyKwh,
                      planner_type: isManualAlgorithm ? selectedAlgorithm : 'perfect_foresight_peak_shave'
                    }];
                    console.log(`[Optimization] Manual size: ${powerKw}kW / ${energyKwh}kWh, algorithm=${requestBody.manual_configurations[0].planner_type}`);
                  } else {
                    // Auto size: generate sizes; sweep algorithms only if user did not pick one
                    requestBody.auto_generate = true;
                    requestBody.num_sizes = 6;
                    console.log(`[Optimization] Auto size: 6 sizes, algorithmMode=${algorithmMode}, planner=${requestBody.planner_type}`);
                  }

                  console.log('[Optimization] Request body:', JSON.stringify(requestBody, null, 2));

                  // IMPORTANT: Clear any stale run_id BEFORE starting new optimization
                  // This prevents race conditions with StrictMode double-invoking effects
                  sessionStorage.removeItem('optimization_run_id');

                  // Wait for backend to confirm optimization started before navigating
                  // This ensures the progress state is initialized before the results page starts polling
                  const startOpt = () =>
                    fetch(`${API_BASE_URL}/optimization/multi-config`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify(requestBody)
                    });

                  let response = await startOpt();
                  let responseData = await response.json();

                  // 409 = a previous optimization is still marked as running (e.g. the Render
                  // worker died mid-run and the Redis state never got cleared). Offer to
                  // force-clear and retry instead of leaving the user stuck until the 30-min
                  // auto-expire.
                  if (response.status === 409 && currentProject?.id) {
                    const confirmed = window.confirm(
                      t(
                        'optimizationAnalysis.confirm.cancelAndRetry',
                        'Hay una optimización marcada como en curso para este proyecto. ¿Cancelarla y reiniciar?'
                      )
                    );
                    if (!confirmed) {
                      setIsStartingOptimization(false);
                      return;
                    }
                    try {
                      await fetch(
                        `${API_BASE_URL}/optimization/force-clear/${currentProject.id}`,
                        { method: 'POST', credentials: 'include' }
                      );
                    } catch (clearErr) {
                      console.error('Failed to force-clear stuck optimization:', clearErr);
                    }
                    response = await startOpt();
                    responseData = await response.json();
                  }

                  if (!response.ok) {
                    console.error('Multi-config optimization failed:', responseData);
                    toast.error(`Optimization failed to start: ${responseData.error || 'Unknown error'}`);
                    setIsStartingOptimization(false);
                    return; // Don't navigate if optimization failed to start
                  }

                  // Save the run_id to prevent race condition with stale error state
                  // This allows the results page to ignore errors from previous runs
                  if (responseData.data?.run_id) {
                    sessionStorage.setItem('optimization_run_id', responseData.data.run_id);
                    console.log('[Optimization] Saved run_id:', responseData.data.run_id);
                  }

                  // Backend confirmed optimization started - now safe to navigate
                  navigate('/optimization/results?running=true');
                } catch (error) {
                  console.error('Error initiating optimization:', error);
                  toast.error('Failed to connect to optimization server');
                  setIsStartingOptimization(false);
                }
              }}
              className="flex items-center gap-2"
              disabled={!projectData?.consumption || !projectData?.prices || isStartingOptimization}
            >
              {isStartingOptimization ? (
                <>
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  {t('optimizationAnalysis.buttons.starting', 'Starting...')}
                </>
              ) : (
                <>
                  <Battery className="w-5 h-5" />
                  {t('optimizationAnalysis.buttons.runSimulation')}
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {(isLoadingProjectData || isLoadingConfig) ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">{t('optimizationAnalysis.loading.projectData')}</p>
            </div>
          </CardContent>
        </Card>
      ) : !projectData?.consumption || !projectData?.bill || !projectData?.prices ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('optimizationAnalysis.noData.title')}</h3>
              <p className="text-gray-600 mb-4">
                {t('optimizationAnalysis.noData.message')}
              </p>
              <Button
                variant="primary"
                onClick={() => navigate('/optimization/configure')}
              >
                {t('optimizationAnalysis.noData.button')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : isAnalyzing ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">{t('optimizationAnalysis.loading.analyzing')}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Theoretical Maximum Section */}
          {activeSection === 'theoretical' && (
            <TheoreticalMaximumSection
              prices={projectData?.prices}
              consumption={projectData?.consumption}
              bill={projectData?.bill}
              configuration={configuration}
              exchangeRate={exchangeRate}
            />
          )}

          {/* Algorithm Section */}
          {activeSection === 'algorithm' && (
            <AlgorithmSection
              bill={projectData?.bill}
              configuration={configuration}
              consumption={projectData?.consumption}
            />
          )}
        </>
      )}
    </div>
  );
}